import { useEffect, useMemo, useState } from "react";
import { Alert, Box, InputAdornment, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Search as SearchIcon } from "@mui/icons-material";
import { ILiberacaoBaseAntiga, listarLiberacoesBaseAntiga } from "services/modules/liberacoes";

/**
 * Plano 22/10/2026 (rev. 2, Frente 4) — pedido P3 do PO: mostrar no MOF novo o que o cliente tem na
 * BASE ANTIGA (CEM Desktop antigo, SeriaisPortfolios), separado em Cadastro N1 e N2. Só consulta: o
 * legado continua entregando esses pacotes; nada aqui grava ou remove.
 */

interface SecaoBaseAntigaProps {
  idSerial: number;
  nivel: 1 | 2;
}

const formatarData = (valor?: string | null) =>
  valor ? new Date(valor).toLocaleDateString("pt-BR") : "-";

export function SecaoBaseAntiga({ idSerial, nivel }: SecaoBaseAntigaProps) {
  const [itens, setItens] = useState<ILiberacaoBaseAntiga[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    let ativo = true;
    if (!idSerial) {
      setItens([]);
      return;
    }
    setCarregando(true);
    setErro(null);
    listarLiberacoesBaseAntiga(idSerial, nivel)
      .then((dados) => {
        if (ativo) setItens(dados);
      })
      .catch((mensagem: string) => {
        if (ativo) {
          setItens([]);
          setErro(mensagem);
        }
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [idSerial, nivel]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return itens;
    return itens.filter((i) =>
      [i.tipoPacote, i.projetista, i.referencia, i.linha, i.acesso].some((v) => (v ?? "").toLowerCase().includes(termo))
    );
  }, [itens, busca]);

  const colunasN2: GridColDef[] = [
    { field: "projetista", headerName: "Projetista", flex: 1, minWidth: 160 },
    { field: "linha", headerName: "Linha/Sistema", flex: 1, minWidth: 160 },
    { field: "acesso", headerName: "Acesso", width: 130 },
    {
      field: "dataAtualizacaoPacote",
      headerName: "Atualização do pacote",
      width: 180,
      valueFormatter: (p) => formatarData(p.value as string),
    },
  ];

  const colunasN1: GridColDef[] = [
    { field: "tipoPacote", headerName: "Tipo de Pacote", width: 150 },
    { field: "referencia", headerName: "Referência", flex: 1, minWidth: 160 },
    { field: "linha", headerName: "Linha", flex: 1, minWidth: 140, valueFormatter: (p) => (p.value as string) || "-" },
    { field: "acesso", headerName: "Acesso", width: 130 },
    {
      field: "dataAtualizacaoPacote",
      headerName: "Atualização do pacote",
      width: 180,
      valueFormatter: (p) => formatarData(p.value as string),
    },
  ];

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#3C90AD" }}>
        Liberado no CEM Desktop antigo (base antiga) — somente consulta {carregando ? "" : `(${itens.length})`}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Pacotes que este cliente já tem pelo legado. Eles continuam disponíveis para ele e não são alterados por esta tela.
      </Typography>

      {erro ? (
        <Alert severity="error">{erro}</Alert>
      ) : (
        <>
          <TextField
            size="small"
            placeholder="Buscar na base antiga"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setPage(0);
            }}
            sx={{ mb: 1, width: 320, maxWidth: "100%" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* DataGrid direto: paginacao e ordenacao nativas, no navegador, sobre todas as linhas do
              serial (ate 275) — o StripedDataGrid nao repassa page/pageSize e cortaria em 100. */}
          <DataGrid
            columns={nivel === 2 ? colunasN2 : colunasN1}
            rows={filtrados}
            getRowId={(row) => row.id}
            loading={carregando}
            autoHeight
            disableSelectionOnClick
            disableColumnMenu
            page={page}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 50, 100]}
            onPageChange={(novaPagina: number) => setPage(novaPagina)}
            onPageSizeChange={(novoTamanho: number) => {
              setPageSize(novoTamanho);
              setPage(0);
            }}
            localeText={{
              noRowsLabel: "Nenhum pacote no CEM Desktop antigo",
              MuiTablePagination: { labelRowsPerPage: "Linhas por página" },
            }}
          />
        </>
      )}
    </Box>
  );
}

export default SecaoBaseAntiga;
